import { Suspense, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float } from "@react-three/drei";

/**
 * A tasteful, corporate-friendly 3D hero scene.
 * - Softly rotating rings + a soft "N" mark blob
 * - Mouse-parallax on camera
 * - Warm bone/mauve palette matched to the brand
 * - Kept subtle: no aggressive motion, no wild colors
 */

function RotatingRing({ position, color, scale = 1, speed = 0.15 }) {
  const ref = useRef(null);
  useFrame((state, dt) => {
    if (!ref.current) return;
    ref.current.rotation.x += dt * speed;
    ref.current.rotation.y += dt * speed * 0.6;
  });
  return (
    <mesh ref={ref} position={position} scale={scale}>
      <torusGeometry args={[0.9, 0.14, 24, 64]} />
      <meshStandardMaterial
        color={color}
        metalness={0.55}
        roughness={0.32}
      />
    </mesh>
  );
}

function CenterBlob({ position, scale = 1 }) {
  const ref = useRef(null);
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.getElapsedTime();
    ref.current.rotation.x = Math.sin(t * 0.2) * 0.3;
    ref.current.rotation.y = t * 0.12;
  });
  return (
    <Float speed={1} rotationIntensity={0.4} floatIntensity={0.6}>
      <mesh ref={ref} position={position} scale={scale}>
        <icosahedronGeometry args={[1, 2]} />
        <meshPhysicalMaterial
          color={"#F1EAEC"}
          roughness={0.15}
          metalness={0.3}
          clearcoat={0.7}
          clearcoatRoughness={0.2}
          reflectivity={0.6}
        />
      </mesh>
    </Float>
  );
}

function Camera({ mouse }) {
  useFrame((state) => {
    const x = mouse.current.x * 0.35;
    const y = mouse.current.y * 0.2;
    state.camera.position.x += (x - state.camera.position.x) * 0.05;
    state.camera.position.y += (y - state.camera.position.y) * 0.05;
    state.camera.lookAt(0, 0, 0);
  });
  return null;
}

export default function Hero3D() {
  const mouse = useRef({ x: 0, y: 0 });

  const onPointerMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouse.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.current.y = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
  };

  const dpr = useMemo(
    () => (typeof window !== "undefined" ? Math.min(window.devicePixelRatio, 1.5) : 1),
    []
  );

  return (
    <div
      className="absolute inset-0 pointer-events-auto"
      onPointerMove={onPointerMove}
      aria-hidden
    >
      <Canvas
        dpr={dpr}
        camera={{ position: [0, 0, 7], fov: 42 }}
        gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
        style={{ background: "transparent" }}
      >
        <Suspense fallback={null}>
          <Camera mouse={mouse} />
          <ambientLight intensity={0.55} />
          <directionalLight position={[4, 4, 5]} intensity={0.9} color={"#FFE7D9"} />
          <directionalLight position={[-5, -2, -3]} intensity={0.4} color={"#C9A56A"} />
          <pointLight position={[0, 3, 2]} intensity={0.5} color={"#8E4A6E"} />

          <group position={[0.6, 0, 0]}>
            <Float speed={0.7} rotationIntensity={0.6} floatIntensity={0.9}>
              <RotatingRing position={[-1.4, 1.0, -0.5]} color={"#8E4A6E"} scale={0.75} speed={0.25} />
            </Float>
            <Float speed={0.9} rotationIntensity={0.5} floatIntensity={0.7}>
              <RotatingRing position={[1.6, 0.2, -0.3]} color={"#B7864A"} scale={0.55} speed={0.18} />
            </Float>
            <Float speed={0.5} rotationIntensity={0.3} floatIntensity={0.4}>
              <RotatingRing position={[-0.2, -1.4, 0.2]} color={"#A9718B"} scale={0.5} speed={0.3} />
            </Float>
            <Float speed={0.8} rotationIntensity={0.4} floatIntensity={0.5}>
              <RotatingRing position={[1.2, -1.5, 0.3]} color={"#8E4A6E"} scale={0.4} speed={0.22} />
            </Float>
          </group>

          <fog attach="fog" args={["#FAF7F5", 5, 11]} />
        </Suspense>
      </Canvas>
    </div>
  );
}
